import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import {
  AlertCircle,
  ExternalLink,
  Info,
  Loader2,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useUIStore } from '@/stores/useUIStore'
import {
  formatCad,
  useBillingSummary,
  useCancelPendingChange,
  useTextosFacturationUsage,
  type BillingSummary,
  type BillingSummaryResponse,
} from '@/api/billing'
import { Switch } from '@/components/ui/switch'
import { useApiError } from '@/hooks/useApiError'
import { BuyAiPackDialog } from './BuyAiPackDialog'

/**
 * Récap "Mon abonnement" (Mode 1 de BillingView).
 *
 * Lit /api/billing/summary/ et rend :
 *  - Bandeau d'état contextuel (trialing / past_due / canceled)
 *  - Badge "Plan legacy" + bandeau bleu si is_legacy_plan
 *  - Récap (Plan / Cycle / Utilisateurs / Numéros / Total / Prochain)
 *  - Boutons Changer de plan / Switch cycle / Comparer
 *
 * Si pas de subscription, affiche un fallback simple "Aucun abonnement actif"
 * avec CTA "Comparer les plans".
 */
export function MySubscriptionCard() {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const [, setSearchParams] = useSearchParams()

  const { data, isLoading, isError } = useBillingSummary()
  const [achatPack, setAchatPack] = useState(false)
  const textosMutation = useTextosFacturationUsage()
  const { handleError } = useApiError()

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }
  if (isError) {
    return (
      <div
        className={cn(
          'rounded-xl border p-6 text-sm',
          darkMode
            ? 'border-red-500/30 bg-red-500/10 text-red-300'
            : 'border-red-200 bg-red-50 text-red-700',
        )}
      >
        {t(
          'manager.billing.errorLoad',
          'Impossible de charger votre abonnement.',
        )}
      </div>
    )
  }

  const noSub = !data?.has_subscription || !data?.summary
  if (noSub) {
    return <NoSubscriptionState locale={locale} darkMode={darkMode} aiMinutes={data?.ai_minutes ?? null} />
  }

  const sub = data!.summary!
  const billing = data!

  /** The plan change screen; `cycle` opens it on that cycle (the "Switch to
   *  monthly/yearly" button goes through the same change as any plan). */
  const openChangePlan = (cycle?: 'monthly' | 'yearly') => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('action', 'change-plan')
      if (cycle) params.set('cycle', cycle)
      else params.delete('cycle')
      return params
    })
  }

  return (
    <>
      {/* Bandeau d'état contextuel selon subscription_status */}
      <StatusBanner billing={billing} darkMode={darkMode} locale={locale} />

      {/* Baisse de plan programmée au renouvellement */}
      {sub.pending_change && (
        <PendingChangeBanner change={sub.pending_change} darkMode={darkMode} locale={locale} />
      )}

      {/* Bandeau "Plan legacy" (discret, bleu) */}
      {sub.is_legacy_plan && (
        <LegacyBanner darkMode={darkMode} locale={locale} />
      )}

      {/* Bloc principal : récap */}
      <div
        className={cn(
          'rounded-xl border p-6 space-y-5',
          darkMode
            ? 'border-slate-800 bg-slate-900'
            : 'border-slate-200 bg-white',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <h2
            className={cn(
              'text-lg font-semibold',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {t('manager.billing.mySubscription', 'Mon abonnement')}
          </h2>
          {sub.is_legacy_plan && (
            <Badge
              variant="secondary"
              className={cn(
                'text-xs',
                darkMode
                  ? 'bg-slate-800 text-slate-300'
                  : 'bg-slate-100 text-slate-600',
              )}
            >
              {t('manager.billing.legacyPlan', 'Plan legacy')}
            </Badge>
          )}
        </div>

        {/* Lignes du récap */}
        <div className="space-y-3">
          <Row
            label={t('manager.billing.plan', 'Plan')}
            value={sub.plan_name}
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openChangePlan()}
              >
                {t('manager.billing.changePlanCta', 'Changer de plan')}
              </Button>
            }
            darkMode={darkMode}
          />
          <Row
            label={t('manager.billing.cycle', 'Cycle')}
            value={
              sub.billing_interval === 'year'
                ? t('manager.billing.yearly', 'Annuel (-20%)')
                : sub.billing_interval === 'month'
                  ? t('manager.billing.monthly', 'Mensuel')
                  : t('manager.billing.unknownInterval', 'Cycle inconnu')
            }
            action={
              sub.billing_interval !== 'unknown' &&
              !sub.is_enterprise && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openChangePlan(sub.billing_interval === 'year' ? 'monthly' : 'yearly')}
                >
                  {sub.billing_interval === 'year'
                    ? t('manager.billing.switchToMonthly', 'Passer en mensuel')
                    : t('manager.billing.switchToYearly', 'Passer en annuel')}
                </Button>
              )
            }
            darkMode={darkMode}
          />
          <Row
            label={t('manager.billing.users', 'Utilisateurs')}
            value={t('manager.billing.usersCount', '{{count}} actifs', {
              count: sub.users_count,
            })}
            action={
              <Link
                to={`/${locale}/manager?view=team`}
                className={cn(
                  'inline-flex items-center gap-1 text-sm hover:underline',
                  darkMode ? 'text-blue-400' : 'text-blue-600',
                )}
              >
                <Users className="h-3.5 w-3.5" />
                {t('manager.billing.manageInTeam', 'Gérer')}
              </Link>
            }
            darkMode={darkMode}
          />
          {sub.ai_minutes && (
            <div className="space-y-1">
              <Row
                label={t('manager.billing.aiMinutes', 'Minutes IA')}
                value={
                  <span className="inline-flex items-center gap-3 min-w-0">
                    <span>
                      {sub.ai_minutes.included_minutes === null
                        ? t('manager.billing.aiMinutesUnlimited', '{{used}} min ce mois-ci', { used: sub.ai_minutes.used_minutes })
                        : t('manager.billing.aiMinutesUsed', '{{used}} / {{included}} min', { used: sub.ai_minutes.used_minutes, included: sub.ai_minutes.included_minutes })}
                    </span>
                    {sub.ai_minutes.included_minutes !== null && sub.ai_minutes.included_minutes > 0 && (
                      <span className={cn('h-1.5 w-24 rounded-full overflow-hidden shrink-0', darkMode ? 'bg-slate-700' : 'bg-slate-200')}>
                        <span
                          className={cn('block h-full rounded-full', sub.ai_minutes.used_minutes >= sub.ai_minutes.included_minutes ? 'bg-amber-500' : 'bg-blue-500')}
                          style={{ width: `${Math.min(100, Math.round((sub.ai_minutes.used_minutes / sub.ai_minutes.included_minutes) * 100))}%` }}
                        />
                      </span>
                    )}
                  </span>
                }
                action={
                  billing.subscription_status === 'trialing' ? undefined : (
                  <button
                    type="button"
                    onClick={() => setAchatPack(true)}
                    className={cn('inline-flex items-center gap-1 text-sm hover:underline', darkMode ? 'text-blue-400' : 'text-blue-600')}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {t('manager.billing.buyPack', 'Acheter un pack')}
                  </button>
                  )
                }
                darkMode={darkMode}
              />
              <p className={cn('text-xs pl-[8.75rem]', darkMode ? 'text-slate-500' : 'text-slate-400')}>
                {sub.ai_minutes.prepaid_minutes > 0 && (
                  <>{t('manager.billing.aiMinutesPrepaid', '{{count}} min prépayées', { count: sub.ai_minutes.prepaid_minutes })} · </>
                )}
                {sub.ai_minutes.overage_minutes > 0 && sub.ai_minutes.overage_amount_cad && (
                  <span className={darkMode ? 'text-amber-400' : 'text-amber-600'}>
                    {t('manager.billing.aiMinutesOverage', '{{count}} min au-delà, {{amount}} sur la prochaine facture', {
                      count: sub.ai_minutes.overage_minutes, amount: formatCad(sub.ai_minutes.overage_amount_cad),
                    })}{' · '}
                  </span>
                )}
                {t('manager.billing.aiMinutesResets', 'Remise à zéro le {{date}}', {
                  date: new Date(sub.ai_minutes.period_end).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', { day: 'numeric', month: 'short' }),
                })}
              </p>
            </div>
          )}
          {sub.textos && (
            <div className="space-y-1">
              <Row
                label={t('manager.billing.textos', 'Textos')}
                value={
                  <span className="inline-flex items-center gap-3 min-w-0">
                    <span>{t('manager.billing.textosUsed', '{{used}} / {{included}} ce mois-ci', { used: sub.textos.used, included: sub.textos.included })}</span>
                    <span className={cn('h-1.5 w-24 rounded-full overflow-hidden shrink-0', darkMode ? 'bg-slate-700' : 'bg-slate-200')}>
                      <span
                        className={cn('block h-full rounded-full', sub.textos.used >= sub.textos.included ? 'bg-amber-500' : 'bg-blue-500')}
                        style={{ width: `${Math.min(100, Math.round((sub.textos.used / Math.max(1, sub.textos.included)) * 100))}%` }}
                      />
                    </span>
                  </span>
                }
                action={
                  <label className={cn('inline-flex items-center gap-2 text-sm cursor-pointer', darkMode ? 'text-slate-300' : 'text-slate-700')}>
                    <Switch
                      checked={sub.textos.usage_billing}
                      disabled={textosMutation.isPending}
                      onCheckedChange={(v) => textosMutation.mutate(v, { onError: (e) => handleError(e) })}
                    />
                    {t('manager.billing.textosUsageBilling', 'Au-delà, {{price}} le texto', { price: formatCad(sub.textos.overage_rate_cad) })}
                  </label>
                }
                darkMode={darkMode}
              />
              <p className={cn('text-xs pl-[8.75rem]', darkMode ? 'text-slate-500' : 'text-slate-400')}>
                {t('manager.billing.textosRule', 'Illimités dans l’application. Les envois automatisés (API, Claude, ChatGPT, Zapier) sont compris jusqu’au plafond.')}{' '}
                {sub.textos.blocked && (
                  <span className={darkMode ? 'text-amber-400' : 'text-amber-600'}>
                    {t('manager.billing.textosBlocked', 'Plafond atteint : les envois automatisés sont arrêtés jusqu’au {{date}}, ou activez la facturation à l’usage.', {
                      date: new Date(sub.textos.period_end).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', { day: 'numeric', month: 'short' }),
                    })}{' '}
                  </span>
                )}
                {sub.textos.overage > 0 && sub.textos.usage_billing && (
                  <span className={darkMode ? 'text-amber-400' : 'text-amber-600'}>
                    {t('manager.billing.textosOverage', '{{count}} au-delà, {{amount}} sur la prochaine facture', {
                      count: sub.textos.overage, amount: formatCad(sub.textos.overage_amount_cad),
                    })}{' '}
                  </span>
                )}
              </p>
            </div>
          )}
          <div className="space-y-1">
            <Row
              label={t('manager.billing.numbers', 'Numéros')}
              value={t('manager.billing.numbersCount', '{{count}} actifs', {
                count: sub.numbers_count,
              })}
              action={
                <Link
                  to={`/${locale}/manager?view=numbers`}
                  className={cn(
                    'inline-flex items-center gap-1 text-sm hover:underline',
                    darkMode ? 'text-blue-400' : 'text-blue-600',
                  )}
                >
                  <Settings className="h-3.5 w-3.5" />
                  {t('manager.billing.manageInNumbers', 'Gérer')}
                </Link>
              }
              darkMode={darkMode}
            />
            {sub.extra_numbers > 0 && (
              <p
                className={cn(
                  'text-xs pl-2 border-l-2',
                  darkMode
                    ? 'border-slate-700 text-slate-400'
                    : 'border-slate-200 text-slate-500',
                )}
              >
                ↳{' '}
                {t(
                  'manager.billing.numbersBreakdown',
                  '{{included}} inclus + {{extra}} add-on à {{price}}/mois chacun',
                  {
                    included: sub.included_numbers,
                    extra: sub.extra_numbers,
                    price: formatCad(sub.extra_number_unit_price_cad),
                  },
                )}
              </p>
            )}
          </div>
        </div>

        {/* Total Stripe + prochain prélèvement */}
        <div
          className={cn(
            'pt-4 border-t space-y-2',
            darkMode ? 'border-slate-800' : 'border-slate-200',
          )}
        >
          {sub.welcome_credit && !sub.is_enterprise && (
            <div>
              <div
                className={cn(
                  'flex items-baseline justify-between gap-3 text-sm',
                  darkMode ? 'text-emerald-300' : 'text-emerald-700',
                )}
              >
                <span>{t('manager.billing.welcomeCredit', 'Crédit de bienvenue, référence partenaire')}</span>
                <span className="font-medium tabular-nums">-{formatCad(sub.welcome_credit.amount_cad)}</span>
              </div>
              <p className={cn('text-xs', darkMode ? 'text-slate-400' : 'text-slate-500')}>
                {t('manager.billing.welcomeCreditHint', 'Déjà déduit de votre prochaine facture.')}
              </p>
            </div>
          )}
          <div className="flex items-baseline justify-between gap-3">
            <span
              className={cn(
                'text-sm font-medium',
                darkMode ? 'text-slate-300' : 'text-slate-700',
              )}
            >
              {t('manager.billing.nextInvoice', 'Prochaine facture')}
            </span>
            <span
              className={cn(
                'text-2xl font-semibold tabular-nums',
                darkMode ? 'text-white' : 'text-slate-900',
              )}
            >
              {sub.is_enterprise
                ? t('manager.billing.contactSales', 'Sur devis')
                : sub.next_invoice_amount_cad
                  ? formatCad(sub.next_invoice_amount_cad)
                  : '—'}
            </span>
          </div>
          {billing.current_period_end && !billing.cancel_at_period_end && (
            <p
              className={cn(
                'text-xs',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {t('manager.billing.nextBilling', 'Prochain prélèvement')} :{' '}
              {new Date(billing.current_period_end).toLocaleDateString(
                locale === 'fr' ? 'fr-CA' : 'en-CA',
                { year: 'numeric', month: 'long', day: 'numeric' },
              )}
            </p>
          )}
        </div>

        {/* CTA compare */}
        <a
          href={`${import.meta.env.VITE_MARKETING_URL}/${locale}/pricing`}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'inline-flex items-center gap-1.5 text-sm font-medium hover:underline',
            darkMode ? 'text-blue-400' : 'text-blue-600',
          )}
        >
          {t('manager.billing.compareAllPlans', 'Comparer tous les plans')}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      <BuyAiPackDialog open={achatPack} onClose={() => setAchatPack(false)} darkMode={darkMode} />
    </>
  )
}

// =============================================================================
// Sous-composants internes
// =============================================================================

function Row({
  label,
  value,
  action,
  darkMode,
}: {
  label: string
  value: React.ReactNode
  action?: React.ReactNode
  darkMode: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-baseline gap-3 min-w-0">
        <span
          className={cn(
            'text-sm w-32 shrink-0',
            darkMode ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            'text-sm font-medium truncate',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {value}
        </span>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  )
}

function StatusBanner({
  billing,
  darkMode,
  locale,
}: {
  billing: BillingSummaryResponse
  darkMode: boolean
  locale: string
}) {
  const { t } = useTranslation()
  const status = billing.subscription_status

  if (status === 'trialing' && billing.days_until_trial_end != null) {
    return (
      <div
        className={cn(
          'rounded-lg border p-4 flex items-start gap-3',
          darkMode
            ? 'border-blue-500/30 bg-blue-500/10'
            : 'border-blue-200 bg-blue-50',
        )}
      >
        <Info
          className={cn(
            'h-5 w-5 flex-shrink-0 mt-0.5',
            darkMode ? 'text-blue-400' : 'text-blue-600',
          )}
        />
        <p
          className={cn(
            'text-sm',
            darkMode ? 'text-blue-200' : 'text-blue-900',
          )}
        >
          {t(
            'manager.billing.trialBanner',
            'Essai actif - il vous reste {{days}} jours.',
            { days: billing.days_until_trial_end },
          )}
        </p>
      </div>
    )
  }
  if (status === 'past_due' || status === 'unpaid') {
    return (
      <div
        className={cn(
          'rounded-lg border p-4 flex items-start gap-3',
          darkMode
            ? 'border-red-500/30 bg-red-500/10'
            : 'border-red-200 bg-red-50',
        )}
      >
        <AlertCircle
          className={cn(
            'h-5 w-5 flex-shrink-0 mt-0.5',
            darkMode ? 'text-red-400' : 'text-red-600',
          )}
        />
        <div className="flex-1">
          <p
            className={cn(
              'text-sm font-medium',
              darkMode ? 'text-red-200' : 'text-red-900',
            )}
          >
            {t(
              'manager.billing.pastDueBanner',
              'Paiement échoué. Mettez à jour votre moyen de paiement pour réactiver votre abonnement.',
            )}
          </p>
        </div>
      </div>
    )
  }
  if (status === 'canceled' || billing.cancel_at_period_end) {
    return (
      <div
        className={cn(
          'rounded-lg border p-4 flex items-start gap-3',
          darkMode
            ? 'border-amber-500/30 bg-amber-500/10'
            : 'border-amber-200 bg-amber-50',
        )}
      >
        <AlertCircle
          className={cn(
            'h-5 w-5 flex-shrink-0 mt-0.5',
            darkMode ? 'text-amber-400' : 'text-amber-600',
          )}
        />
        <div className="flex-1">
          <p
            className={cn(
              'text-sm',
              darkMode ? 'text-amber-200' : 'text-amber-900',
            )}
          >
            {billing.current_period_end
              ? t(
                  'manager.billing.canceledBanner',
                  'Annulé - actif jusqu\'au {{date}}.',
                  {
                    date: new Date(
                      billing.current_period_end,
                    ).toLocaleDateString(
                      locale === 'fr' ? 'fr-CA' : 'en-CA',
                      { year: 'numeric', month: 'long', day: 'numeric' },
                    ),
                  },
                )
              : t('manager.billing.canceledBannerNoDate', 'Abonnement annulé.')}
          </p>
        </div>
      </div>
    )
  }
  return null
}

function LegacyBanner({
  darkMode,
  locale,
}: {
  darkMode: boolean
  locale: string
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'rounded-lg border p-4 flex items-start gap-3',
        darkMode
          ? 'border-blue-500/30 bg-blue-500/10'
          : 'border-blue-200 bg-blue-50',
      )}
    >
      <Info
        className={cn(
          'h-5 w-5 flex-shrink-0 mt-0.5',
          darkMode ? 'text-blue-400' : 'text-blue-600',
        )}
      />
      <p
        className={cn(
          'text-sm',
          darkMode ? 'text-blue-200' : 'text-blue-900',
        )}
      >
        {t(
          'manager.billing.legacyBanner',
          'Vous êtes sur un ancien plan. Découvrez la nouvelle grille tarifaire pour profiter des dernières fonctions.',
        )}{' '}
        <a
          href={`${import.meta.env.VITE_MARKETING_URL}/${locale}/pricing`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline"
        >
          {t('manager.billing.compareNewPlans', 'Comparer les nouveaux plans')}{' '}
          <ExternalLink className="h-3 w-3 inline-block ml-0.5" />
        </a>
      </p>
    </div>
  )
}

function NoSubscriptionState({
  locale,
  darkMode,
  aiMinutes,
}: {
  locale: string
  darkMode: boolean
  aiMinutes: BillingSummary['ai_minutes']
}) {
  const { t } = useTranslation()
  const [, setSearchParams] = useSearchParams()
  return (
    <div
      className={cn(
        'rounded-xl border p-8 text-center space-y-4',
        darkMode
          ? 'border-slate-800 bg-slate-900'
          : 'border-slate-200 bg-white',
      )}
    >
      {aiMinutes && (
        <p className={cn('text-sm', darkMode ? 'text-slate-300' : 'text-slate-600')}>
          {t('manager.billing.aiMinutes', 'Minutes IA')} : {t('manager.billing.aiMinutesPayg', '{{used}} min ce mois-ci, à l’usage', { used: aiMinutes.used_minutes })}
        </p>
      )}
      <h2
        className={cn(
          'text-lg font-semibold',
          darkMode ? 'text-white' : 'text-slate-900',
        )}
      >
        {t('manager.billing.noSubscription', 'Aucun abonnement actif')}
      </h2>
      <p
        className={cn(
          'text-sm',
          darkMode ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {t(
          'manager.billing.noSubscriptionDesc',
          'Choisissez un plan pour activer votre compte.',
        )}
      </p>
      <div className="flex items-center justify-center gap-2">
        <Button
          type="button"
          onClick={() =>
            setSearchParams((prev) => {
              const params = new URLSearchParams(prev)
              params.set('action', 'change-plan')
              return params
            })
          }
        >
          {t('manager.billing.choosePlan', 'Choisir un plan')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            window.open(
              `${import.meta.env.VITE_MARKETING_URL}/${locale}/pricing`,
              '_blank',
              'noopener,noreferrer',
            )
          }
        >
          {t('manager.billing.compareAllPlans', 'Comparer tous les plans')}
        </Button>
      </div>
    </div>
  )
}


/** La baisse de plan attend le renouvellement ; le client peut l'annuler. */
function PendingChangeBanner({
  change,
  darkMode,
  locale,
}: {
  change: NonNullable<BillingSummary['pending_change']>
  darkMode: boolean
  locale: string
}) {
  const { t } = useTranslation()
  const { handleError } = useApiError()
  const annuler = useCancelPendingChange()
  const date = change.effective_at
    ? new Date(change.effective_at).toLocaleDateString(
        locale === 'fr' ? 'fr-CA' : 'en-CA',
        { year: 'numeric', month: 'long', day: 'numeric' },
      )
    : null

  return (
    <div
      className={cn(
        'rounded-lg border p-4 flex items-center gap-3',
        darkMode ? 'border-slate-700 bg-slate-800/60' : 'border-slate-200 bg-slate-50',
      )}
    >
      <Info
        className={cn('h-5 w-5 flex-shrink-0', darkMode ? 'text-slate-400' : 'text-slate-500')}
      />
      <p className={cn('flex-1 text-sm', darkMode ? 'text-slate-200' : 'text-slate-800')}>
        {date
          ? t('manager.billing.pendingChange.banner', 'Passage à {{plan}} le {{date}}.', {
              plan: change.plan_name,
              date,
            })
          : t('manager.billing.pendingChange.bannerNoDate', 'Passage à {{plan}} au renouvellement.', {
              plan: change.plan_name,
            })}
      </p>
      <Button
        variant="outline"
        size="sm"
        disabled={annuler.isPending}
        onClick={() => annuler.mutate(undefined, { onError: (e) => handleError(e) })}
      >
        {annuler.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {t('manager.billing.pendingChange.cancel', 'Annuler')}
      </Button>
    </div>
  )
}
